import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Lightbulb, Code, TrendingUp, ArrowRight, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const CATEGORIES = [
  {
    id: 'innovation',
    icon: Lightbulb,
    color: '#8DC21F',
    gradient: 'linear-gradient(135deg, rgba(141,194,31,0.1), transparent)',
    titleES: 'Reto de Innovación',
    titleEN: 'Innovation Challenge',
    whenES: 'Cuando no existe una solución conocida en el mercado',
    whenEN: 'When there is no known solution in the market',
    characteristicsES: [
      'Problema ambiguo o mal definido',
      'Requiere exploración y experimentación',
      'Solución no evidente → necesita descubrimiento',
      'Alto grado de incertidumbre técnica',
    ],
    characteristicsEN: [
      'Ambiguous or ill-defined problem',
      'Requires exploration and experimentation',
      'Solution not obvious → needs discovery',
      'High degree of technical uncertainty',
    ],
    examplesES: 'Outputs de planta sin mercado, captura de CO₂, batalla social biometano',
    examplesEN: 'Plant outputs without market, CO₂ capture, biomethane social battle',
    vehicleES: 'Pilotos, innovación abierta, fondos EU',
    vehicleEN: 'Pilots, open innovation, EU funds',
  },
  {
    id: 'it',
    icon: Code,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, rgba(59,130,246,0.1), transparent)',
    titleES: 'IT / Digitalización',
    titleEN: 'IT / Digitalization',
    whenES: 'Cuando la tecnología es madura y está lista para implementar',
    whenEN: 'When technology is mature and ready to implement',
    characteristicsES: [
      'Tecnología probada y estable',
      'Solución conocida en el mercado',
      'Riesgo técnico bajo o controlado',
      'Implementación directa → sin fase exploratoria',
    ],
    characteristicsEN: [
      'Proven and stable technology',
      'Known solution in the market',
      'Low or controlled technical risk',
      'Direct implementation → no exploratory phase',
    ],
    examplesES: 'ERP, CRM, sensores IoT estándar, plataformas cloud',
    examplesEN: 'ERP, CRM, standard IoT sensors, cloud platforms',
    vehicleES: 'Compra directa, integración con IT',
    vehicleEN: 'Direct purchase, IT integration',
  },
  {
    id: 'improvement',
    icon: TrendingUp,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, rgba(245,158,11,0.1), transparent)',
    titleES: 'Mejora Continua',
    titleEN: 'Continuous Improvement',
    whenES: 'Cuando se optimizan procesos o sistemas existentes',
    whenEN: 'When optimizing existing processes or systems',
    characteristicsES: [
      'Proceso o sistema ya existe',
      'Objetivo: incrementar eficiencia',
      'No requiere tecnología nueva',
      'Metodologías: Lean, Six Sigma, Kaizen',
    ],
    characteristicsEN: [
      'Process or system already exists',
      'Goal: increase efficiency',
      'Does not require new technology',
      'Methodologies: Lean, Six Sigma, Kaizen',
    ],
    examplesES: 'Optimización de rutas, reducción de tiempos, mejora de procedimientos operativos',
    examplesEN: 'Route optimization, time reduction, operational procedure improvement',
    vehicleES: 'Equipos internos, consultoría',
    vehicleEN: 'Internal teams, consulting',
  },
];

function CategoryCard({ category, index, inView, lang }) {
  const Icon = category.icon;
  const title = lang === 'es' ? category.titleES : category.titleEN;
  const when = lang === 'es' ? category.whenES : category.whenEN;
  const characteristics = lang === 'es' ? category.characteristicsES : category.characteristicsEN;
  const examples = lang === 'es' ? category.examplesES : category.examplesEN;
  const vehicle = lang === 'es' ? category.vehicleES : category.vehicleEN;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      className="group relative h-full"
    >
      <div
        className="relative h-full rounded-2xl p-6 overflow-hidden transition-all duration-500"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(12px)',
        }}
      >
        {/* Gradient overlay */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: category.gradient }}
        />

        {/* Content */}
        <div className="relative z-10">
          {/* Icon & Title */}
          <div className="flex items-center gap-4 mb-6">
            <div 
              className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
              style={{
                background: `${category.color}15`,
                border: `1px solid ${category.color}30`,
              }}
            >
              <Icon size={22} style={{ color: category.color }} />
            </div>
            <h3 className="text-xl font-semibold text-white leading-tight">
              {title}
            </h3>
          </div>

          {/* When to use */}
          <div className="mb-6 pb-6 border-b border-white/5">
            <div className="text-xs font-bold text-white/40 uppercase tracking-wider mb-2">
              {lang === 'es' ? 'Cuándo usar' : 'When to use'}
            </div>
            <p className="text-sm font-medium" style={{ color: category.color }}>
              {when}
            </p>
          </div>

          {/* Characteristics */}
          <div className="mb-6">
            <div className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3">
              {lang === 'es' ? 'Características' : 'Characteristics'}
            </div>
            <ul className="space-y-2">
              {characteristics.map((char, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-white/60">
                  <CheckCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: category.color, opacity: 0.5 }} />
                  <span>{char}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Examples */}
          <div 
            className="mb-4 p-3 rounded-xl"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <div className="text-xs font-bold text-white/40 uppercase tracking-wider mb-2">
              {lang === 'es' ? 'Ejemplos' : 'Examples'}
            </div>
            <p className="text-xs text-white/60 leading-relaxed">
              {examples}
            </p>
          </div>

          {/* Vehicle */}
          <div className="flex items-center gap-2">
            <ArrowRight size={14} style={{ color: category.color }} />
            <span className="text-xs font-medium text-white/50">
              {vehicle}
            </span>
          </div>
        </div>

        {/* Hover border */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ 
            border: `1px solid ${category.color}40`,
          }}
        />
      </div>
    </motion.div>
  );
}

export default function InnovationFrameworkSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { lang } = useLanguage();

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-32 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #000 0%, rgba(3,8,8,0.98) 50%, #000 100%)',
      }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ 
          background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(245,158,11,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="v-container relative z-10">
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-4"
        >
          <span className="v-tag" style={{ 
            background: 'rgba(245,158,11,0.1)', 
            color: '#f59e0b',
            border: '1px solid rgba(245,158,11,0.25)' 
          }}>
            {lang === 'es' ? 'MARCO DE INNOVACIÓN' : 'INNOVATION FRAMEWORK'}
          </span>
        </motion.div>

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight">
              {lang === 'es' ? (
                <>
                  No todo es{' '}
                  <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>innovación</em>
                  {'. '}Y está{' '}
                  <span style={{ color: '#f59e0b' }}>bien</span>
                </>
              ) : (
                <>
                  Not everything is{' '}
                  <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>innovation</em>
                  {'. '}And that's{' '}
                  <span style={{ color: '#f59e0b' }}>okay</span>
                </>
              )}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <p className="text-base text-white/60 leading-relaxed mb-4">
              {lang === 'es'
                ? 'En Valoriza entendemos la diferencia entre innovación, IT y mejora continua. Saber cuándo usar cada enfoque es tan importante como ejecutarlo bien. Esta claridad nos hace más eficientes y estratégicos.'
                : 'At Valoriza we understand the difference between innovation, IT and continuous improvement. Knowing when to use each approach is as important as executing it well. This clarity makes us more efficient and strategic.'}
            </p>
            <p className="text-sm text-white/50 leading-relaxed">
              {lang === 'es'
                ? 'Llamar "innovación" a todo diluye el término. Aquí definimos con precisión qué es un reto de innovación y qué no lo es.'
                : 'Calling everything "innovation" dilutes the term. Here we precisely define what an innovation challenge is and what it is not.'}
            </p>
          </motion.div>
        </div>

        {/* Category cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CATEGORIES.map((category, index) => (
            <CategoryCard
              key={category.id}
              category={category}
              index={index}
              inView={inView}
              lang={lang}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-12 text-center"
        >
          <div 
            className="inline-block px-6 py-4 rounded-2xl max-w-3xl"
            style={{
              background: 'rgba(245,158,11,0.05)',
              border: '1px solid rgba(245,158,11,0.15)',
            }}
          >
            <p className="text-sm text-white/70 leading-relaxed">
              {lang === 'es'
                ? '💡 Los 8 retos priorizados para 2026 son auténticos retos de innovación. Requieren exploración, incertidumbre gestionada y aprendizaje iterativo. No son proyectos IT ni iniciativas de mejora continua.'
                : '💡 The 8 challenges prioritized for 2026 are authentic innovation challenges. They require exploration, managed uncertainty and iterative learning. They are not IT projects or continuous improvement initiatives.'}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
